"use client"

import "./globals.css"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { RefreshCw, AlertTriangle } from "lucide-react"
import posthog from "posthog-js"
import { useLogger } from "@/lib/use-logger"
import { isWebpackError, getErrorTypeForAnalytics } from "@/utils/webpack-error-detector"
import type { GlobalErrorBoundaryProps } from "@/types/error-boundary"
import DonistaLogo from "@/components/icons/donista-logo"

const strings = {
  de: {
    error: {
      title: "Etwas ist schiefgelaufen",
      message: "Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es erneut.",
      reload: "Seite neu laden",
      retry: "Erneut versuchen",
    },
    webpack: {
      title: "App wird aktualisiert",
      message: "Eine neue Version wurde bereitgestellt. Die Seite muss neu geladen werden.",
      reload: "Jetzt neu laden",
      retry: "Nochmal versuchen",
    },
  },
  en: {
    error: {
      title: "Something went wrong",
      message: "An unexpected error occurred. Please try again.",
      reload: "Reload Page",
      retry: "Try Again",
    },
    webpack: {
      title: "App is updating",
      message: "A new version has been deployed. The page needs to be reloaded.",
      reload: "Reload now",
      retry: "Try again",
    },
  },
} as const

function detectLocale(): "de" | "en" {
  if (typeof window === "undefined") return "de"
  const path = window.location.pathname
  return path.startsWith("/en/") || path === "/en" ? "en" : "de"
}

export default function GlobalError({ error, reset }: GlobalErrorBoundaryProps) {
  const log = useLogger(import.meta.url)
  const [locale] = useState(detectLocale)

  const isWebpackErrorDetected = isWebpackError(error)
  const label = isWebpackErrorDetected ? strings[locale].webpack : strings[locale].error

  useEffect(() => {
    posthog.captureException(error, {
      $set: {
        error_type: getErrorTypeForAnalytics(error, "global"),
        error_location: "global_boundary",
      },
    })
    log.error("Global error:", error)
  }, [log, error])

  const handleReload = () => {
    posthog.capture("global_error_hard_refresh", {
      error_type: isWebpackErrorDetected ? "webpack" : "other",
    })
    window.location.reload()
  }

  const handleReset = () => {
    posthog.capture("global_error_reset", {
      error_type: isWebpackErrorDetected ? "webpack" : "other",
    })
    reset()
  }

  return (
    <html lang={locale}>
      <body className="antialiased">
        <style>{`
          @keyframes errorFadeUp {
            from { opacity: 0; transform: translateY(24px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .error-fade-up {
            opacity: 0;
            animation: errorFadeUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
        `}</style>
        <div
          className="min-h-screen flex flex-col items-center justify-center text-center px-4"
          style={{
            backgroundImage: "url('/background/ffflux.svg')",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
          }}
        >
          <div className="error-fade-up">
            <DonistaLogo className="h-10 w-10 mx-auto mb-14 animate-[pulse_4s_ease-in-out_infinite]" />
          </div>

          <div
            className="mb-6 rounded-2xl bg-destructive/8 p-5 error-fade-up"
            style={{ animationDelay: "0.15s" }}
          >
            <AlertTriangle className="h-10 w-10 text-destructive" />
          </div>

          <h1
            className="text-3xl md:text-4xl font-bold mb-3 error-fade-up"
            style={{ animationDelay: "0.3s" }}
          >
            {label.title}
          </h1>
          <p
            className="text-lg text-muted-foreground max-w-md mb-8 error-fade-up"
            style={{ animationDelay: "0.4s" }}
          >
            {label.message}
          </p>

          <div
            className="flex flex-col sm:flex-row gap-3 error-fade-up"
            style={{ animationDelay: "0.5s" }}
          >
            <Button onClick={handleReload} size="lg" className="group px-8">
              <RefreshCw className="h-4 w-4 mr-2 group-hover:animate-spin" />
              {label.reload}
            </Button>
            <Button onClick={handleReset} variant="outline" size="lg" className="px-8">
              {label.retry}
            </Button>
          </div>
        </div>
      </body>
    </html>
  )
}
